import { useQuery } from '@tanstack/react-query';
import { CcmStatuses } from 'infrastructure/enums';
import { showResult } from 'infrastructure/hooks/utils';
import { useEffect, useMemo } from 'react';
import dataService from 'services/dataService';
import { apiDashboardNextAppointments } from 'services/dataService/resources';
import { getLanguageLabelById } from 'utils';

import {
  buildAppointmentMethodTag,
  buildPatientName,
  formatAppointmentTimeCSTLabel,
  formatAppointmentTimeLabel,
  formatBirthDate,
} from '../utils/appointment-formatters';
import { getTodayRangeTimestamps } from '../utils/time-range';

import type { TTodayAppointmentItem } from 'components/molecules/oneplaform/today-appointments/types';
import type { TFunction } from 'i18next';

const TODAY_APPOINTMENTS_QUERY_KEY = 'home-dashboard/today-appointments';
type SortOrder =
  | 'asc'
  | 'desc'
  | 'asc_nulls_first'
  | 'asc_nulls_last'
  | 'desc_nulls_first'
  | 'desc_nulls_last';

const TODAY_APPOINTMENTS_SORT: readonly [string, SortOrder] = [
  'scheduledDate',
  'asc',
];

type AppointmentMethodApi =
  | 'phone_or_video_conference'
  | 'sms'
  | 'email'
  | string;

type ScheduledWith = {
  guid: string;
  name: string;
  type?: string;
};

type PatientInfo = {
  guid: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  birthDate?: string;
  language?: string;
  ccmStatus?: CcmStatuses;
};

type TodayAppointmentApiItem = {
  guid: string;
  patientGuid: string;
  scheduledDate?: string;
  appointmentMethod?: AppointmentMethodApi;
  scheduledWith?: ScheduledWith;
  patient?: PatientInfo;
};

type TodayAppointmentsResponse = {
  count?: number;
  page?: number;
  items?: number;
  data?: TodayAppointmentApiItem[];
};

type FetchTodayAppointmentsParams = {
  page: number;
  items: number;
  dateFrom: number;
  dateTo: number;
  excludedAppointmentGuids?: string;
};

export type UseTodayAppointmentsOptions = {
  page: number;
  items: number;
  dateFrom: number;
  dateTo: number;
  t: TFunction;
  nextAppointmentId?: string;
};

const formatAppointments = (
  items: TodayAppointmentApiItem[] | undefined,
  t: TFunction,
): TTodayAppointmentItem[] => {
  if (!items?.length) return [];

  return items.map((item) => {
    const methodTag = buildAppointmentMethodTag(item.appointmentMethod, t);
    const normalizedPatientGuid = item.patient?.guid?.trim();

    return {
      id: item.guid,
      patientGuid: normalizedPatientGuid || item.patientGuid || '',
      patientName: buildPatientName(item.patient),
      birthDateLabel: formatBirthDate(item.patient?.birthDate),
      languageLabel: item.patient?.language
        ? getLanguageLabelById(item.patient.language)
        : undefined,
      methodTag,
      timeLabel: formatAppointmentTimeLabel(item.scheduledDate, t),
      subtimeLabel: formatAppointmentTimeCSTLabel(item.scheduledDate, t),
      isCcmEnrolled: item.patient?.ccmStatus === CcmStatuses.Enrolled,
    };
  });
};

const fetchTodayAppointments = async (
  params: FetchTodayAppointmentsParams,
): Promise<TodayAppointmentsResponse> => {
  const {
    now,
    from: nextAppointmentDateFrom,
    to: nextAppointmentDateTo,
  } = getTodayRangeTimestamps();
  const endpoint = apiDashboardNextAppointments({
    page: Math.max(params.page, 1),
    items: params.items,
    nextAppointmentDateFrom,
    nextAppointmentDateTo,
    sort: JSON.stringify(TODAY_APPOINTMENTS_SORT),
    excludedAppointmentGuids: params.excludedAppointmentGuids,
    currentDate: now,
  });

  const { data, error } = await dataService.getList<TodayAppointmentsResponse>(
    endpoint,
  );

  if (error) {
    throw new Error(error);
  }

  return (
    data ?? {
      count: 0,
      page: params.page,
      items: params.items,
      data: [],
    }
  );
};

export const useTodayAppointments = (options: UseTodayAppointmentsOptions) => {
  const normalizedPage = Math.max(options.page, 1);
  const normalizedItems = Math.max(options.items, 1);
  const normalizedNextAppointmentId = options.nextAppointmentId?.trim();

  const queryKey = [
    TODAY_APPOINTMENTS_QUERY_KEY,
    normalizedPage,
    normalizedItems,
    options.dateFrom,
    options.dateTo,
    normalizedNextAppointmentId,
  ];

  const queryResult = useQuery<TodayAppointmentsResponse, Error>({
    queryKey,
    queryFn: () =>
      fetchTodayAppointments({
        page: normalizedPage,
        items: normalizedItems,
        dateFrom: options.dateFrom,
        dateTo: options.dateTo,
        excludedAppointmentGuids: normalizedNextAppointmentId,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (queryResult.error) {
      showResult(queryResult.error.message);
    }
  }, [queryResult.error]);

  const appointments = useMemo(
    () => formatAppointments(queryResult.data?.data, options.t),
    [queryResult.data?.data, options.t],
  );

  return {
    appointments,
    rawAppointments: queryResult.data?.data,
    total: queryResult.data?.count ?? 0,
    page: normalizedPage,
    pageSize: normalizedItems,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    refetch: queryResult.refetch,
  };
};
